import React, { useCallback, useMemo, useState } from 'react';
import { ChevronLeft, FileText, Mail, Printer, SlidersHorizontal } from 'lucide-react';
import { TanTable } from '../../components/TanTable';
import { useToast } from '../../contexts/ToastContext';
import DocsFilterBar from '../pro/components/docs/DocsFilterBar';
import DocPreviewLightbox from '../pro/components/docs/DocPreviewLightbox';
import { previewDocumentBlob } from '../../services/documentsSearch.service';
import type {
  BulkEmailResponse, BulkItem, DocumentSearchFilters, DocumentSearchRow,
} from '../../types/documentsSearch';
import BulkEmailModal from './BulkEmailModal';
import {
  useAdditionalFieldColumns, useAdditionalFieldSelection,
} from '../../hooks/useAdditionalFields';
import type { BulkAction } from '../../components/TableActionBar';
import {
  buildDocumentColumns, DEFAULT_DOCUMENT_FILTERS, documentFiltersActive,
  DOCUMENT_COLUMN_FILTER_TYPES, DOCUMENT_ROW_KEYS, rowsToBulkItems,
  useDocumentBulkOps, useDocumentSearch, useDocumentTypes,
} from './documentsGridKit';

/**
 * A dashboard card's table, repainted with document results.
 *
 * This is deliberately not SearchDocumentsView embedded in a card. That view owns a
 * page: its own filter bar, its own grid, its own sticky selection footer — dropped
 * into a card it reads as a table inside a table. Here the card keeps ONE table; only
 * its rows and columns change, and everything else stays the card chrome the user
 * already knows (title, toolbar, column picker, action bar).
 *
 * Two consequences of that choice, both deliberate:
 *
 * * No checkbox column. Email / ZIP / Print run against the table's currently-visible
 *   rows, exactly like Change All and Trace All do in the same action bar, so every
 *   action on a card means the same thing. TanTable's own search and column filters
 *   are how you narrow that set.
 * * The date-range / doc-type / revision filters live in a COLLAPSIBLE sub-header bar
 *   with its own toolbar icon, alongside the action bar's. They are request
 *   parameters, not grid filters — they decide what is fetched, which is exactly why
 *   they matter (they are also how you stay under the row ceiling on a large PRO set)
 *   and why they can't live in the action bar, which acts on rows already fetched.
 *   Collapsed by default so a short dashboard tile spends its height on rows; the
 *   toggle carries a dot whenever a filter is narrowing the table.
 */
export interface CardDocumentsTableProps {
  /** The PROs whose documents to show — captured from the card at switch time. */
  pros: number[];
  /** The card's own title, so the repainted table still identifies its card. */
  cardTitle: string;
  /** TanTable settings key for the documents layout (one per dashboard). */
  settingsKey: string;
  onProClick?: (pronumber: string) => void;
  defaultRecipient?: string;
  /** Switches the card back to its orders grid. */
  onBackToOrders: () => void;
  initialSettings?: any;
  settingsReady?: boolean;
  settingsLoadFailed?: boolean;
  onSettingsSaved?: (key: string, settings: any) => void;
}

const CardDocumentsTable: React.FC<CardDocumentsTableProps> = ({
  pros, cardTitle, settingsKey, onProClick, defaultRecipient = '', onBackToOrders,
  initialSettings, settingsReady, settingsLoadFailed, onSettingsSaved,
}) => {
  const { showToast } = useToast();
  const [previewing, setPreviewing] = useState<DocumentSearchRow | null>(null);
  const [emailItems, setEmailItems] = useState<BulkItem[] | null>(null);
  const [filters, setFilters] = useState<DocumentSearchFilters>(DEFAULT_DOCUMENT_FILTERS);

  // Added order columns, keyed to this card's own layout key so each dashboard card
  // remembers its own. Same mechanism as the Search Results Documents view.
  const docFields = useAdditionalFieldSelection(settingsKey);

  const { rows, loading, error, truncated, reload } = useDocumentSearch(
    pros, filters,
    { additionalFields: docFields.additionalFields, ready: docFields.ready },
  );
  const ops = useDocumentBulkOps();
  const docTypesQuery = useDocumentTypes();

  // No `selection` — see the component doc: actions are scoped to visible rows.
  const baseColumns = useMemo(
    () => buildDocumentColumns({ onProClick, onPreview: setPreviewing }),
    [onProClick],
  );
  const { columns, availableFields } = useAdditionalFieldColumns<DocumentSearchRow>(
    docFields, { baseColumns, coveredFields: DOCUMENT_ROW_KEYS },
  );

  /**
   * Guard a bulk action against its server-side ceiling before sending.
   *
   * Returns the items when the set is within cap, else toasts and returns null. The
   * caps are the bulk endpoints' own; past them Email/ZIP get a raw validation error
   * and Print 413s after the user has already waited out a merge.
   */
  const itemsWithinCap = useCallback((
    docRows: DocumentSearchRow[],
    kind: 'bulk' | 'print',
  ): BulkItem[] | null => {
    if (docRows.length === 0) {
      showToast({ type: 'info', message: 'No documents in the current view.' });
      return null;
    }
    const over = kind === 'print'
      ? ops.overPrintCap(docRows.length) || ops.overBulkCap(docRows.length)
      : ops.overBulkCap(docRows.length);
    if (over) {
      showToast({ type: 'warning', message: ops.capMessage(docRows.length) ?? 'Too many documents.' });
      return null;
    }
    return rowsToBulkItems(docRows);
  }, [ops, showToast]);

  const handleSent = useCallback((res: BulkEmailResponse) => {
    const to = res.recipients.join(', ');
    showToast({
      message: res.failed.length > 0
        ? `${res.sent} document(s) emailed to ${to}. `
          + `${res.failed.length} could not be sent — see the list.`
        : `${res.sent} document(s) emailed to ${to}.`,
      type: res.failed.length > 0 ? 'warning' : 'success',
    });
    // Only auto-close on a clean send: when some documents failed the modal is the
    // only place naming WHICH ones, so leave it up for the user to read.
    if (res.failed.length === 0) setEmailItems(null);
  }, [showToast]);

  /**
   * Blob loader for the lightbox, keyed on the row being previewed.
   *
   * The generic /documents/preview route is used instead of the per-PRO one because a
   * row can come from any of the three document sources and only `cms` rows exist in
   * cms_map. Memoized on doc_key so the lightbox's fetch effect doesn't re-run on
   * every parent render.
   */
  const loadPreviewBlob = useMemo(
    () => (previewing
      ? () => previewDocumentBlob({
        source: previewing.source, ref: previewing.ref,
        fileinfo_recno: previewing.fileinfo_recno,
        pronumber: previewing.pronumber,
      })
      : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewing?.doc_key],
  );

  const busy = ops.busy !== null;

  const bulkActions = useMemo<BulkAction[]>(() => [
    {
      key: 'back-to-orders',
      label: 'Orders',
      icon: <ChevronLeft className="h-4 w-4" />,
      title: 'Switch this card back to its orders',
      onRun: () => onBackToOrders(),
    },
    {
      key: 'docs-email',
      label: 'Email…',
      icon: <Mail className="h-4 w-4" />,
      disabled: busy,
      title: 'Email the documents visible in this table',
      onRun: (docRows: DocumentSearchRow[]) => {
        const items = itemsWithinCap(docRows, 'bulk');
        if (items) setEmailItems(items);
      },
    },
    {
      key: 'docs-zip',
      label: ops.busy === 'zip' ? 'Zipping…' : 'Download ZIP',
      icon: <FileText className="h-4 w-4" />,
      disabled: busy,
      title: 'Download the documents visible in this table as a ZIP',
      onRun: (docRows: DocumentSearchRow[]) => {
        const items = itemsWithinCap(docRows, 'bulk');
        if (items) void ops.runZip(items);
      },
    },
    {
      key: 'docs-print',
      label: ops.busy === 'print' ? 'Preparing…' : 'Print',
      icon: <Printer className="h-4 w-4" />,
      disabled: busy,
      title: 'Merge the documents visible in this table into one PDF',
      onRun: (docRows: DocumentSearchRow[]) => {
        const items = itemsWithinCap(docRows, 'print');
        if (items) ops.runPrint(items);
      },
    },
  ], [busy, ops, itemsWithinCap, onBackToOrders]);

  return (
    <div className="w-full h-full">
      <TanTable<DocumentSearchRow>
        data={rows}
        columns={columns}
        loading={loading}
        errorMessage={error}
        onRetry={reload}
        // The card's title, not "Documents": the user needs to know WHICH card this is,
        // and the columns already make it obvious they are looking at documents. The
        // headerExtra pill names the mode.
        cardTitle={cardTitle}
        headerExtra={
          <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:border-slate-600 dark:bg-slate-700/50 dark:text-slate-200">
            <FileText className="h-3 w-3" aria-hidden />
            Documents · {pros.length.toLocaleString()} PRO{pros.length === 1 ? '' : 's'}
            {truncated && (
              <span
                className="text-amber-700 dark:text-amber-400"
                title="The document list hit its row ceiling — narrow the PRO set to see the rest."
              >
                · capped
              </span>
            )}
          </span>
        }
        bulkActions={bulkActions}
        subHeaderBar={{
          icon: <SlidersHorizontal className="h-4 w-4" aria-hidden />,
          label: 'Document Filters',
          active: documentFiltersActive(filters),
          content: (
            <DocsFilterBar
              value={filters}
              onChange={setFilters}
              docTypes={docTypesQuery.data || []}
            />
          ),
        }}
        settingsKey={settingsKey}
        fieldName={settingsKey}
        availableFields={availableFields}
        additionalFields={docFields.additionalFields}
        onAdditionalFieldsChange={docFields.onAdditionalFieldsChange}
        initialSettings={initialSettings}
        settingsReady={settingsReady}
        settingsLoadFailed={settingsLoadFailed}
        onSettingsSaved={onSettingsSaved}
        searchPlaceholder="Filter documents..."
        enableColumnFilters
        // Text filters, not dropdowns — see DOCUMENT_COLUMN_FILTER_TYPES.
        columnFilterTypes={DOCUMENT_COLUMN_FILTER_TYPES}
        enableGrouping={false}
        enablePagination={false}
        fillContainer
        getRowId={(row) => row.doc_key}
      />

      {emailItems && (
        <BulkEmailModal
          items={emailItems}
          defaultRecipient={defaultRecipient}
          onClose={() => setEmailItems(null)}
          onSent={handleSent}
        />
      )}

      {previewing && (
        <DocPreviewLightbox
          pronumber={previewing.pronumber}
          loadBlob={loadPreviewBlob}
          filename={previewing.filename}
          filetype={previewing.filetype}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
};

export default CardDocumentsTable;
